import { Grid, Stack, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CancelALert from "../../../components/alert/CancelAlert";
import AppLoader from "../../../components/apploader";
import CustomContainer from "../../../components/container/CustomContainer";
import DashboardHeader from "../../../components/dashboardHeader";
import DynamicTabsDrawer from "../../../components/drawer";
import ReviewDialog from "../../../components/reviewDialog";
import DynamicTable from "../../../components/table";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { setActiveTab } from "../../../lib/redux/slices/activeTabSlice";
import { CURRENCY } from "../../../lib/variables";
import { getOrderDetail, updateOrderStatus } from "../../../services/orderFn";
import { renderSubTotal } from "../../../utils";
import { t } from "i18next";
import InvoiceViewer from "./InvoiceViewer";

const tabs = [
  {
    label: "myCart",
    value: "ecommerce_cart",
  },
  {
    label: "purchaseInvoice",
    value: "purchase_invoice",
  },
];

const headerData = [
  "name",
  "quantity",
  "price",
  "finalPrice",
  "size",
  "color",
  "weight",
  "package",
  "status",
  "review",
  "deliveredDate",
  "cancelProduct",
];

const tableRows = [
  "product_name",
  "selectedQty",
  "product_final_amount",
  "product_final_price",
  "product_selected_size",
  "product_selected_color",
  "product_selected_weight",
  "product_package",
  "product_status",
  "show_review_btn",
  "product_delivered_at",
  "show_cancel_btn",
];

const LocalContainer = ({ children, ...props }) => {
  return (
    <Stack
      p={{ xs: 1, md: 2 }}
      border={"1px solid"}
      borderColor={"text.borderGrey"}
      borderRadius={1}
      {...props}
    >
      {children}
    </Stack>
  );
};

const CustomChip = ({ children, ...props }) => {
  return (
    <Stack
      px={1}
      py={0.3}
      borderRadius={1}
      width={"max-content"}
      direction={"row"}
      alignItems={"center"}
      bgcolor={"text.borderGrey"}
      {...props}
    >
      {children}
    </Stack>
  );
};

const TotalRow = ({ keyyy, value, isLast }) => {
  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      margin={"8px 0 4px 0"}
    >
      <Typography
        variant={"body1"}
        fontSize={isLast ? "1.125rem" : "1rem"}
        fontWeight={isLast ? 500 : 400}
        color={"text.black"}
      >
        {t(keyyy)}
      </Typography>
      <Typography
        variant={"body1"}
        fontSize={isLast ? "1.125rem" : "1rem"}
        fontWeight={isLast ? 500 : 400}
        color={"text.black"}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const OrderDetailPage = () => {
  const openReviewDialog = useRef(null);
  const openCancelAlert = useRef();
  const { orderId } = useParams();
  const dispacth = useDispatch();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [userAddress, setUserAddress] = useState(null);
  const [allOrders, setAllOrders] = useState();
  const activeTab = useSelector((state) => state.activeTabReducer.tabs);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const handleChangeTab = (value) => {
    navigate("/dashboard/e-commerce");
    dispacth(setActiveTab({ ...activeTab, user_cart: value }));
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail();
    }
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getOrderDetail(orderId);
      if (response?.data.status === "success") {
        let data = response?.data?.data;
        setOrderDetail(data);
        setUserAddress(data.address);
        setAllOrders(data.order);
      } else {
        showSnackbar(response?.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenReviewDialog = (row, index) => {
    openReviewDialog.current.openDialog({
      data: row,
      getData: fetchOrderDetail,
      index,
      orderId: orderDetail._id,
      type: "product",
    });
  };

  const renderPerCentageAmount = (params) => {
    const total = renderSubTotal(params);
    return Math.round((total * orderDetail?.tax) / 100);
  };

  const finalAmount =
    parseInt(renderSubTotal(orderDetail?.order) || 0) +
    parseInt(renderPerCentageAmount(orderDetail?.order) || 0) +
    parseInt(orderDetail?.shippingFee || 0) +
    parseInt(orderDetail?.platformFee || 0);

  const handleOpenCancelAlert = (row, index) => {
    openCancelAlert.current.openDialog({
      warningText: t("cancelProductMsg"),
      data: row,
      index,
    });
  };

  const handleUpdated = async (params) => {
    let updatedOrders = [...allOrders];
    updatedOrders[params.index].orderStatus = "User Cancelled";

    let payload = {
      order: updatedOrders,
      userId: orderDetail?.userId?._id,
    };

    try {
      setIsLoading(true);
      const response = await updateOrderStatus(orderDetail._id, payload);

      if (response?.data.status === "success") {
        fetchOrderDetail();
        openCancelAlert.current.closeDialog();
        showSnackbar(response.data.message, "success");
      } else {
        showSnackbar(response?.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const headerData = [
    "Name",
    "Quantity",
    "Price",
    "Final Price",
    "Size",
    "Color",
    "Weight",
    "Package",
    "Status",
    "Delievered Date",
    ...(!orderDetail?.isPackageOrder ? ["Review", "Cancel Product"] : []),
  ];

  const tableRows = [
    "product_name",
    "selectedQty",
    "product_final_amount",
    "product_final_price",
    "product_selected_size",
    "product_selected_color",
    "product_selected_weight",
    "product_package",
    "product_status",
    "product_delivered_at",
    ...(!orderDetail?.isPackageOrder
      ? ["show_review_btn", "show_cancel_btn"]
      : []),
  ];

  console.log(orderDetail,"sadkjsadjksadksjadskjadsajkdkj");

  return (
    <DashboardHeader>
      <AppLoader isVisible={isLoading} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={2.5}>
          <DynamicTabsDrawer
            primaryHeading={t("eComPurchasing")}
            tabs={tabs}
            activeTab={"purchase_invoice"}
            showBackBtn={true}
            handleBack={() => navigate("/dashboard")}
            handleChangeTab={handleChangeTab}
          />
        </Grid>
        <Grid item xs={12} md={9.5}>
          <CustomContainer
            primaryHeading={"Order Detail"}
            secondaryHeading={t("viewOrderStatus")}
            showBackButton={true}
            handleBack={() => {
              navigate("/dashboard/e-commerce");
              handleChangeTab("purchase_invoice");
            }}
          >
            <LocalContainer>
              <Typography variant="h5" fontWeight={600}>
                {`${t("orderId")} :#${orderDetail?.orderId || ""}`}
              </Typography>

              <CustomChip mt={0.5}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={"text.slateGrey"}
                >
                  {t("placedOn")} :
                </Typography>
                <Typography variant="body2" color={"text.slateGrey"}>
                  {moment(orderDetail?.createdAt).format("MM-DD-YYYY")}
                </Typography>
              </CustomChip>
            </LocalContainer>

            {userAddress && (
              <LocalContainer mt={2}>
                <Typography variant="h5" fontWeight={600}>
                  {t("shippingInfo")}
                </Typography>
                <Typography variant="body1">{`${userAddress?.firstname} ${userAddress?.lastname}`}</Typography>
                <Typography variant="body1">{userAddress?.contact}</Typography>
                <Typography variant="body2">{userAddress?.email}</Typography>
                <Typography variant="body2">{userAddress?.address}</Typography>

                <Typography variant="body2" color={"text.coolGrey"}>
                  {userAddress?.country} / {userAddress?.state} /{" "}
                  {userAddress?.city} / {userAddress?.zipCode}
                </Typography>
              </LocalContainer>
            )}
            {allOrders?.length > 0 && (
              <LocalContainer mt={2}>
                <Typography variant="h5" fontWeight={600} mb={2}>
                  {t("itemsOrdered")}
                </Typography>

                <DynamicTable
                  tableProps={{
                    sx: { minWidth: 1400 },
                  }}
                  tableHeader={headerData}
                  displayRows={tableRows}
                  tableData={allOrders || []}
                  handleReview={handleOpenReviewDialog}
                  handleCancelOrder={handleOpenCancelAlert}
                />
              </LocalContainer>
            )}
            {userAddress && (
              <LocalContainer mt={2}>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  textTransform={"capitalize"}
                >
                  {t("billing")}
                </Typography>
                <TotalRow
                  keyyy={"subtotal"}
                  value={`${CURRENCY} ${renderSubTotal(orderDetail?.order)}`}
                />
                <TotalRow
                  keyyy={"shippingFee"}
                  value={`${CURRENCY} ${orderDetail?.shippingFee}`}
                />
                <TotalRow keyyy={"tax"} value={`${orderDetail?.tax}%`} />
                <TotalRow
                  keyyy={"taxAmount"}
                  value={`${CURRENCY} ${renderPerCentageAmount(
                    orderDetail?.order
                  )}`}
                />
                {/* <TotalRow
                  keyyy={"Platorm Percentage"}
                  value={`${orderDetail.platformPercentage}%`}
                /> */}
                <TotalRow
                  keyyy={"platformCharges"}
                  value={`${CURRENCY} ${orderDetail?.platformFee}`}
                />
                <TotalRow
                  keyyy={"total"}
                  value={`${CURRENCY} ${finalAmount}`}
                  isLast={true}
                />
              </LocalContainer>
            )}
            <InvoiceViewer
              invoiceData={orderDetail}
              showDownload={true}
              showPreview={true}
            />
          </CustomContainer>
        </Grid>
      </Grid>
      <ReviewDialog ref={openReviewDialog} />
      <CancelALert ref={openCancelAlert} onCancel={handleUpdated} />
    </DashboardHeader>
  );
};

export default OrderDetailPage;
